import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { doc, getDoc } from "firebase/firestore";

import { TableVirtuoso, VirtuosoHandle } from "react-virtuoso";

import { Divider, Link, TableCell, TableRow, TextField } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

import { Button, Tooltip, IconButton } from "@mui/material";
import { Box, Card, CardHeader, CardContent } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/InfoOutlined";

import AddApplicantDialog from "../dialogs/dialogApplicant";

import {
  APPLICANT_DOCUMENT_KIND_LABELS,
  Applicant,
  ApplicantDoc,
  ApplicantDocument,
  ApplicantsDict,
  PROPERTY_MAPPING,
} from "../../types/applicants";
import { copyToClipboard, isArray, isObject } from "../../utils/utils";
import { TitledPage } from "../../components/titled-page";
import { AuthContext } from "../../components/auth-provider";
import {
  deleteApplicant,
  generateApplicantEmailExtensionCode,
  loadApplicants,
  storeApplicant,
} from "../../data-functions/applicants-api";
import {
  deleteApplications,
  loadApplications,
} from "../../data-functions/applications-api";
import { SponsorsDict } from "../../types/sponsors";
import { AgentsDict } from "../../types/agents";
import { loadSponsors } from "../../data-functions/system-data_api";
import { loadAgents } from "../../data-functions/agent-api";
import { HeadCells, Order, getComparator } from "../../types/tables";
import { EnhancedTableHeadContents } from "../../components/enhanced-table";
import { VirtuosoTableComponents } from "../../components/enhanced-virtuoso-table";
import { SimpleTreeView } from "@mui/x-tree-view";
import { useNavigate, useParams } from "react-router-dom";

import { ApplicationsDict } from "../../types/visa-application";
import { SeasonsDict, WorkOrdersDict } from "../../types/orders";
import { loadSeasons, loadWorkOrders } from "../../data-functions/orders-api";
import { DocumentHistoryDialog } from "../dialogs/dialogDocumentHistory";
import { hasRole } from "../../utils/user-utils";
import { CampaignsDict } from "../../types/recruiting.js";
import {
  loadCampaign,
  loadCandidates,
} from "../../data-functions/recruiting_api";
import { addHOPSApplicants } from "../../utils/exporters/exporters";
import {
  getApplicantDocumentAccessUrl,
  syncApplicantDocumentsFromRecruiting,
} from "../../data-functions/applicant-documents-api";
import { ApplicantTermsAcceptanceSummary } from "../../components/terms/applicant-terms-acceptance-summary";
import db from "../../types/firebase";

const headCells: HeadCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
    sortable: true,
  },
  {
    id: "surname",
    numeric: false,
    disablePadding: false,
    label: "Family Name",
    sortable: true,
  },
  {
    id: "nationality",
    numeric: false,
    disablePadding: false,
    label: "Nationality",
    sortable: true,
    tooltip: "Applicant's nationality",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "",
    sortable: false,
    align: "right",
  },
];

const ApplicantsPage: React.FC = () => {
  const { applicantId } = useParams<{ applicantId?: string }>();

  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [applicants, setApplicants] = useState<ApplicantsDict>({});
  const [selectedApplicantId, setSelectedApplicant] = useState("");
  const [applications, setApplications] = useState<ApplicationsDict>({});
  const [campaigns, setCampaigns] = useState<CampaignsDict>({});
  const [sponsors, setSponsors] = useState<SponsorsDict>({});
  const [agents, setAgents] = useState<AgentsDict>({});
  const [editApplicant, setEditdApplicant] = useState<ApplicantDoc | null>(
    null,
  );
  const [csvMappingEntries, setCsvMappingEntries] = useState<
    Record<string, string>
  >({});
  const [searchValue, setSearchValue] = useState<string>("");

  const [seasons, setSeasons] = useState<SeasonsDict>({});
  const [workOrders, setWorkOrders] = useState<WorkOrdersDict>({});

  const [historyDoc, setHistoryDoc] = useState<string>("");
  const [openHistory, setOpenHistory] = useState(false);
  const [generatingApplicantCodeId, setGeneratingApplicantCodeId] =
    useState<string>("");

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("name");
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const firstMount = useRef(false);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportTotal, setExportTotal] = useState(0);
  const [isMigratingSponsorIds, setIsMigratingSponsorIds] = useState(false);

  const { currentUser } = useContext(AuthContext)!;
  const navigate = useNavigate();
  const isSponsorScopedUser = Boolean(currentUser?.appUser?.sponsorId);

  const fetchSeasons = async () => {
    const seasonsDict = await loadSeasons(currentUser?.appUser);
    setSeasons(seasonsDict);
  };

  const fetchWorkOrders = async () => {
    const workOrdersDict = await loadWorkOrders(currentUser?.appUser);
    setWorkOrders(workOrdersDict);
  };

  const fetchApplicants = async () => {
    const applicantsDict: ApplicantsDict = await loadApplicants(
      currentUser?.appUser,
    );

    setApplicants(applicantsDict);
  };

  const fetchSponsors = async () => {
    const sponsorsDict = await loadSponsors(currentUser?.appUser);

    if (sponsorsDict) {
      setSponsors(sponsorsDict);
    } else {
      setSponsors({});
    }
  };

  const fetchAgents = async () => {
    const agentsDict = await loadAgents(currentUser?.appUser);

    if (agentsDict) {
      setAgents(agentsDict);
    } else {
      setAgents({});
    }
  };

  // useEffect(() => {
  //   if (selectedApplicant && selectedApplicant.id) {
  //     displayApplicantDetails(selectedApplicant);
  //   }
  // }, [applicants]);

  useEffect(() => {
    firstMount.current = true;
    setCsvMappingEntries(PROPERTY_MAPPING);
    fetchSponsors();
    fetchAgents();
    fetchApplicants();
    fetchSeasons();
    fetchWorkOrders();
    // if (applicantId) {
    //   setSearchValue("");
    //   setSelectedApplicant(applicantId);
    // }
  }, []);

  useEffect(() => {
    setSelectedApplicant("");
    setSearchValue("");
    fetchSponsors();
    fetchAgents();
    fetchApplicants();
    // if (applicantId) {
    //   setSearchValue("");
    //   setSelectedApplicant(applicantId);
    // }
  }, [currentUser]);

  useEffect(() => {
    if (editApplicant) {
      handleAddNewApplicant(true);
    }
  }, [editApplicant]);

  const setApplicantCampaigns = async (applicantId: string) => {
    const camp = await loadCurrentCampaigns(applicantId);
    if (camp) {
      setCampaigns(camp);
    }
  };

  useEffect(() => {
    loadCurrentApplications(selectedApplicantId);
    setApplicantCampaigns(selectedApplicantId);
  }, [selectedApplicantId]);

  useEffect(() => {
    const syncSelectedApplicantDocuments = async () => {
      if (!selectedApplicantId || !applicants[selectedApplicantId]) {
        return;
      }

      const syncedApplicant = await syncApplicantDocumentsFromRecruiting(
        currentUser?.appUser,
        {
          id: selectedApplicantId,
          applicant: applicants[selectedApplicantId],
        },
        true,
      );

      if (!syncedApplicant.changed) {
        return;
      }

      setApplicants((currentApplicants) => ({
        ...currentApplicants,
        [selectedApplicantId]: syncedApplicant.applicantDoc.applicant,
      }));
    };

    void syncSelectedApplicantDocuments();
  }, [
    applicants[selectedApplicantId],
    currentUser?.appUser,
    selectedApplicantId,
  ]);

  const loadCurrentApplications = async (applicantId: string) => {
    const applications = await loadApplications(currentUser?.appUser, {
      applicantId,
    });
    setApplications(applications);
  };

  const loadCurrentCampaigns = async (applicantId: string) => {
    console.log("applicantId: ", applicantId);
    if (!(applicantId && applicants[applicantId])) {
      return null;
    }

    const email = applicants[applicantId].email || null;

    if (!email) {
      return {};
    }

    const candidates = await loadCandidates(
      currentUser?.appUser,
      "all_campaigns",
      {
        email: { operator: "==", value: email },
      },
    );
    const campaignIds = Object.entries(candidates).map(
      ([campaignId, candidate]) => {
        return candidate.campaignId;
      },
    );

    const newCampaigns: CampaignsDict = {};
    for (const campaignId of campaignIds) {
      const campaign = campaignId ? await loadCampaign(campaignId) : undefined;

      if (
        campaign &&
        (campaign.campaign.sponsorId === currentUser?.appUser?.sponsorId ||
          !currentUser?.appUser?.sponsorId)
      ) {
        newCampaigns[campaign.id] = campaign.campaign;
      }
    }
    return newCampaigns;
  };

  const loadCurrentCampaignsForExport = async (applicantId: string) => {
    console.log("applicantId: ", applicantId);
    if (!(applicantId && applicants[applicantId])) {
      return [];
    }

    const email = applicants[applicantId].email || null;

    if (!email) {
      return [];
    }

    const newCampaigns: string[] = [];

    const candidates = await loadCandidates(
      currentUser?.appUser,
      "all_campaigns",
      {
        email: { operator: "==", value: email },
      },
    );

    for (const [, candidate] of Object.entries(candidates)) {
      const campaignId = candidate.campaignId;
      if (campaignId) {
        const campaign = campaignId
          ? await loadCampaign(campaignId)
          : undefined;
        if (
          campaign &&
          (campaign.campaign.sponsorId === currentUser?.appUser?.sponsorId ||
            !currentUser?.appUser?.sponsorId)
        ) {
          const interviewer = candidate.interviewer || "n/a";
          newCampaigns.push(campaign.campaign.name + " - " + interviewer);
        }
      }
    }
    return newCampaigns;
  };

  const handleAddNewApplicant = (edit: boolean = false) => {
    setEditMode(edit && !!editApplicant);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditdApplicant(null);
    //fetchApplicants();
  };

  const handleSaveApplicant = async (applicant: ApplicantDoc) => {
    const appDoc = { ...applicant };
    const id = await storeApplicant(currentUser?.appUser, appDoc, true);
    if (!appDoc.id && id) {
      appDoc.id = id;
    }
    const newApplicants = { ...applicants };
    newApplicants[appDoc.id] = appDoc.applicant;

    setApplicants(newApplicants);
  };

  const handleCopyApplicantEmailExtensionCode = (code: string) => {
    if (!code) {
      return;
    }

    copyToClipboard(code);
  };

  const handleGenerateApplicantCode = async (applicantId: string) => {
    if (!applicantId) {
      return;
    }

    setGeneratingApplicantCodeId(applicantId);
    try {
      const applicantDoc = await generateApplicantEmailExtensionCode(
        currentUser?.appUser,
        applicantId,
      );
      if (!applicantDoc) {
        return;
      }

      setApplicants((prevApplicants) => ({
        ...prevApplicants,
        [applicantDoc.id]: applicantDoc.applicant,
      }));
    } finally {
      setGeneratingApplicantCodeId("");
    }
  };

  const handleShowHistory = async (documentId: string) => {
    setHistoryDoc(documentId);
    setOpenHistory(true);
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress(0);
    const total = filteredApplicants.length;
    setExportTotal(total);
    try {
      const appl: any[] = [];
      let processed = 0;
      for (const [applicantId, applicant] of filteredApplicants) {
        const camp = await loadCurrentCampaignsForExport(applicantId);
        appl.push({
          applicant,
          campaigns: camp,
        });
        processed += 1;
        setExportProgress(processed);
      }
      const csvData = addHOPSApplicants(appl);
      const curentDate = new Date().toISOString().split("T")[0];
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = "applicants " + curentDate + ".csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
      setExportTotal(0);
      setExportProgress(0);
    }
  };

  async function deleteLinkedApplications(applicantId: string) {
    const applications = await loadApplications(currentUser?.appUser, {
      applicantId: applicantId,
    });
    await deleteApplications(Object.keys(applications));
  }

  const handleDelete = async (applicantId: string) => {
    if (!applicantId) return;

    if (
      window.confirm(
        "Are you sure you want to delete this applicant and all linked applications?",
      )
    ) {
      deleteLinkedApplications(applicantId);
      await deleteApplicant(applicantId);
      const newApplicants = { ...applicants };
      delete newApplicants[applicantId];
      if (selectedApplicantId === applicantId) {
        navigate("/applicants");
        //setSelectedApplicant("");
      }
      setApplicants(newApplicants);
    }
  };

  const handleSelectApplicant = async (applicantId: string) => {
    if (applicantId) {
      console.log("selected Applicant", applicantId);
      // const loadedApplicant = await loadApplicant(applicant);
      const applicant = applicants[applicantId];
      if (applicant) {
        //setSelectedApplicant(applicantId);
        navigate(`/applicants/${applicantId}`);
      }
    }
  };

  const handleEditApplicant = async (applicant: ApplicantDoc) => {
    if (applicant) {
      setEditdApplicant(applicant);
    }
  };

  const isExternalUrl = (value: unknown) => {
    return typeof value === "string" && /^https?:\/\//.test(value);
  };

  const isApplicantDocumentRecord = (
    value: unknown,
  ): value is ApplicantDocument => {
    return (
      !!value &&
      typeof value === "object" &&
      "kind" in value &&
      "source" in value &&
      ("storagePath" in value || "url" in value)
    );
  };

  const getDisplayLabel = (key: string, fallback: string) => {
    const translatedDocumentLabels: Record<string, string> = {
      uploadedDocuments: "Uploaded documents",
      kind: "Document type",
      source: "Document source",
      url: "Document URL",
      storagePath: "Storage path",
      fileName: "File name",
      contentType: "Content type",
      extension: "File extension",
      uploadedAt: "Uploaded at",
      candidateId: "Candidate ID",
    };

    if (translatedDocumentLabels[key]) {
      return translatedDocumentLabels[key];
    }

    return fallback;
  };

  const handleOpenApplicantDocument = async (
    applicantId: string,
    document: ApplicantDocument,
  ) => {
    if (document.url) {
      window.open(document.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (!(document.storagePath && currentUser?.authUser)) {
      return;
    }

    try {
      const url = await getApplicantDocumentAccessUrl(applicantId, document, {
        authToken: await currentUser.authUser.getIdToken(),
      });

      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Failed to open applicant document", error);
      window.alert("The document could not be opened. Please try again.");
    }
  };

  const formatValueForDisplay = (key: string, value: any) => {
    if (value === undefined || value === null || value === "") {
      return "-";
    }

    if (key === "kind" && typeof value === "string") {
      return (
        APPLICANT_DOCUMENT_KIND_LABELS[
          value as keyof typeof APPLICANT_DOCUMENT_KIND_LABELS
        ] || value
      );
    }

    if (key === "source" && typeof value === "string") {
      return value === "questionnaire" ? "Questionnaire" : "Recruiting";
    }

    return value;
  };

  const renderTreeLabel = (key: string, label: string, value: any) => {
    const formattedValue = formatValueForDisplay(key, value);
    const displayLabel = getDisplayLabel(key, label);

    if (isExternalUrl(value)) {
      return (
        <span>
          {displayLabel}:{" "}
          <Link href={value} target="_blank" rel="noreferrer">
            Open
          </Link>
        </span>
      );
    }

    return `${displayLabel}: ${formattedValue}`;
  };

  const displayNestedObject = (
    parentKey: string,
    idChain: string,
    obj: Object,
  ) => {
    const keys = Object.keys(obj).sort();
    return (
      <TreeItem
        itemId={idChain}
        key={idChain}
        label={getDisplayLabel(
          parentKey,
          csvMappingEntries[parentKey] || parentKey,
        )}
      >
        {keys.map((key) => {
          const value = obj[key as keyof typeof obj] as Object;
          const label = getDisplayLabel(key, csvMappingEntries[key] || key);

          return isObject(value) ? (
            displayNestedObject(key, idChain + "_" + key, value)
          ) : isArray(value) ? (
            displayArray(key, idChain + "_" + key, value as Object[])
          ) : (
            <TreeItem
              itemId={idChain + "_" + key}
              key={idChain + "_" + key}
              label={renderTreeLabel(key, label, value)}
            />
          );
        })}
      </TreeItem>
    );
  };

  const displayUploadedDocuments = (
    applicantId: string,
    documents: ApplicantDocument[],
  ) => {
    if (documents.length === 0) {
      return (
        <Typography color="text.secondary">No uploaded documents</Typography>
      );
    }

    return (
      <SimpleTreeView aria-label="uploaded documents" sx={{ height: "100%" }}>
        {documents.map((document, i) => {
          const documentLabel = formatValueForDisplay("kind", document.kind);

          return (
            <TreeItem
              itemId={`uploadedDocuments_${i}`}
              key={`uploadedDocuments_${i}`}
              label={`${documentLabel}${document.fileName ? `: ${document.fileName}` : ""}`}
            >
              {(document.url || document.storagePath) && (
                <TreeItem
                  itemId={`uploadedDocuments_${i}_open`}
                  key={`uploadedDocuments_${i}_open`}
                  label={
                    <span>
                      Current file:{" "}
                      <Link
                        component="button"
                        onClick={() => {
                          void handleOpenApplicantDocument(
                            applicantId,
                            document,
                          );
                        }}
                      >
                        Open
                      </Link>
                    </span>
                  }
                />
              )}
              {Object.entries(document).map(([key, value]) => {
                if (key === "url" || value === undefined || value === "") {
                  return null;
                }

                return (
                  <TreeItem
                    itemId={`uploadedDocuments_${i}_${key}`}
                    key={`uploadedDocuments_${i}_${key}`}
                    label={renderTreeLabel(
                      key,
                      getDisplayLabel(key, key),
                      value,
                    )}
                  />
                );
              })}
            </TreeItem>
          );
        })}
      </SimpleTreeView>
    );
  };

  const displayArray = (parentKey: string, idChain: string, obj: Object[]) => {
    if (parentKey === "sponsorIds") {
      return (
        <TreeItem
          itemId={idChain}
          key={idChain}
          label={getDisplayLabel(parentKey, "Visible sponsors")}
        >
          {obj.map((element, i) => (
            <TreeItem
              itemId={idChain + "_" + i}
              key={idChain + "_" + i}
              label={renderTreeLabel(
                parentKey,
                `Sponsor ${i + 1}`,
                sponsors[element as string]?.name || element,
              )}
            />
          ))}
        </TreeItem>
      );
    }

    return (
      <TreeItem
        itemId={idChain}
        key={idChain}
        label={getDisplayLabel(
          parentKey,
          csvMappingEntries[parentKey] || parentKey,
        )}
      >
        {obj.map((element, i) => {
          const label = " (" + i + ")";

          return isObject(element) ? (
            displayNestedObject(label, idChain + "_" + i, element)
          ) : isArray(element) ? (
            displayArray(label, idChain + "_" + i, element as Object[])
          ) : (
            <TreeItem
              itemId={idChain + "_" + i}
              key={idChain + "_" + i}
              label={renderTreeLabel(label, label, element)}
            />
          );
        })}
      </TreeItem>
    );
  };

  const displayValue = (key: string, value: any) => {
    if (value === undefined)
      return (
        <TreeItem
          itemId={key}
          key={key}
          label={(csvMappingEntries[key] || key) + ": n/a"}
        />
      );

    switch (key) {
      case "agentId":
        return (
          <TreeItem
            itemId={key}
            key={key}
            label={renderTreeLabel(
              "agentId",
              "Agent",
              (value && agents[value as string]?.company) || "n/a",
            )}
          />
        );
      case "sponsorId":
        return (
          <TreeItem
            itemId={key}
            key={key}
            label={renderTreeLabel(
              "sponsorId",
              "Sponsor",
              sponsors[value as string]?.name || "n/a",
            )}
          />
        );
      default:
        return (
          <TreeItem
            itemId={key}
            key={key}
            label={renderTreeLabel(key, csvMappingEntries[key] || key, value)}
          />
        );
    }
  };

  const displayApplicantDetails = (applicantId: string) => {
    if (!applicantId) {
      return;
    }

    const applicant = applicants[applicantId];

    if (!applicant) {
      return;
    }

    const keys = Object.keys(applicant)
      .filter((key) => {
        if (key === "uploadedDocuments") {
          return false;
        }

        if (
          isSponsorScopedUser &&
          (key === "sponsorId" || key === "sponsorIds")
        ) {
          return false;
        }

        return true;
      })
      .sort();
    const uploadedDocuments = (applicant.uploadedDocuments || []).filter(
      isApplicantDocumentRecord,
    );

    return (
      <>
        {Object.keys(applications).length > 0 && (
          <Card sx={{ marginBottom: "1rem" }}>
            <CardHeader
              title={`Applications (${Object.keys(applications).length})`}
            />
            <CardContent>
              {Object.entries(applications).map(([appId, application]) => {
                return (
                  <Box key={appId} sx={{ display: "flex", gap: "1rem" }}>
                    <div>
                      {application.seasonId
                        ? seasons[application.seasonId].name
                        : "no season"}
                    </div>
                    <div>
                      {application.workOrderId
                        ? workOrders[application.workOrderId].name
                        : "no order assigned"}
                    </div>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        )}

        {Object.keys(campaigns).length > 0 && (
          <Card sx={{ marginBottom: "1rem" }}>
            <CardHeader
              title={`Campaigns (${Object.keys(campaigns).length})`}
            />
            <CardContent>
              {Object.entries(campaigns).map(([campaignId, campaign]) => {
                return <div key={campaignId}>{campaigns[campaignId].name}</div>;
              })}
            </CardContent>
          </Card>
        )}

        <Card sx={{ marginBottom: "1rem" }}>
          <CardHeader title="Email extension code" />
          <CardContent>
            {applicant.email_extension_code ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  {applicant.email_extension_code}
                </Typography>
                <Tooltip title="Copy email code">
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleCopyApplicantEmailExtensionCode(
                        applicant.email_extension_code || "",
                      )
                    }
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Button
                variant="outlined"
                onClick={() => handleGenerateApplicantCode(applicantId)}
                disabled={generatingApplicantCodeId === applicantId}
              >
                Generate code
              </Button>
            )}
          </CardContent>
        </Card>

        <Card sx={{ marginBottom: "1rem" }}>
          <CardHeader title="Terms acceptance" />
          <CardContent>
            <ApplicantTermsAcceptanceSummary applicantId={applicantId} />
          </CardContent>
        </Card>

        <Card sx={{ marginBottom: "1rem" }}>
          <CardHeader
            title={`Uploaded documents (${uploadedDocuments.length})`}
          />
          <CardContent>
            <Box sx={{ maxHeight: "320px", overflowY: "auto" }}>
              {displayUploadedDocuments(applicantId, uploadedDocuments)}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ height: "100%" }}>
          <CardHeader
            title={`Personal information (${applicant.name} ${applicant.surname})`}
          />
          <CardContent>
            <Box sx={{ height: "calc(100vh - 290px)", overflowY: "auto" }}>
              <SimpleTreeView
                aria-label="applicant details"
                sx={{ height: "100%" }}
              >
                {keys.map((key) => {
                  const value = applicant[key as keyof Applicant];

                  return isObject(value)
                    ? displayNestedObject(key, key, value as Object)
                    : isArray(value)
                      ? displayArray(key, key, value as Object[])
                      : displayValue(key, value);
                })}
              </SimpleTreeView>
            </Box>
          </CardContent>
        </Card>
      </>
    );
  };

  /* ------------ Search functions -------------- */

  function descendingComparator(
    a: [string, Applicant],
    b: [string, Applicant],
    orderBy: string,
  ): number {
    const applicant1 = a[1];
    const applicant2 = b[1];

    if (!orderBy) {
      return 0;
    }

    // console.log("a1: ");
    // console.log(applicant1);

    // console.log("a2: ");
    // console.log(applicant2);

    // console.log("orderBy: " + orderBy);

    if (applicant1 && applicant2) {
      let res: number = 0;
      switch (orderBy) {
        case "name":
          const name1 = applicant1.name;
          const name2 = applicant2.name;

          res = name2.localeCompare(name1);
          return res;

        case "surname":
          const surname1 = applicant1.surname;
          const surname2 = applicant2.surname;

          res = surname2.localeCompare(surname1);
          return res;

        case "agent":
          if (applicant1.agentId && applicant2.agentId) {
            const e1 = agents[applicant1.agentId].company;
            const e2 = agents[applicant2.agentId].company;
            res = e2.localeCompare(e1);
            return res;
          } else if (applicant1.sponsorId && !applicant2.sponsorId) {
            return -1;
          } else if (!applicant1.sponsorId && applicant2.sponsorId) {
            return 1;
          } else return 0;

        default:
          break;
      }
    } else {
      if (applicant1 && !applicant2) {
        return -1;
      } else if (!applicant1 && applicant2) {
        return 1;
      }
    }
    return 0;
  }

  const filteredApplicants = useMemo(
    () =>
      Object.entries(applicants).filter(([applicantId, appl]) => {
        const agent = appl.agentId ? agents[appl.agentId]?.company : "n/a";
        try {
          return (
            searchValue.length < 3 ||
            appl.name.toLowerCase().includes(searchValue.toLowerCase()) ||
            appl.surname.toLowerCase().includes(searchValue.toLowerCase()) ||
            appl.email?.toLowerCase().includes(searchValue.toLowerCase()) ||
            appl.nationality?.label
              ?.toLowerCase()
              .includes(searchValue.toLowerCase()) ||
            agent?.toLowerCase().includes(searchValue.toLowerCase())
          );
        } catch (err) {
          console.error("filter exception");
          console.log(appl);
          return false;
        }
      }),
    [searchValue, applicants, sponsors, agents],
  );

  const visibleRows = useMemo(
    () =>
      filteredApplicants
        .slice()
        .sort(
          getComparator(
            order,
            orderBy as keyof Applicant,
            descendingComparator,
          ),
        ),
    [order, orderBy, filteredApplicants],
  );

  const handleSearchValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchValue(event.target.value);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    if (applicantId) {
      const index = visibleRows.findIndex(([ix]) => ix === applicantId);
      if (index === -1 && firstMount.current) {
        setSearchValue("");
      }
      setSelectedApplicant(applicantId);
      if (virtuosoRef.current && firstMount.current) {
        virtuosoRef.current.scrollToIndex({
          index,
          align: "center", // Adjust alignment as needed
          behavior: "auto", // Optional: smooth scrolling
        });
      }
      if (visibleRows.length > 0) {
        firstMount.current = false;
      }
    } else {
      setSelectedApplicant("");
    }
  }, [applicantId, visibleRows]);

  // const handleCallFunction = async () => {
  //   if (!hasRole(currentUser?.appUser, ["admin"])) {
  //     window.alert("You do not have permission to run this migration.");
  //     return;
  //   }

  //   const applicantEntries = Object.entries(applicants);
  //   if (applicantEntries.length === 0) {
  //     window.alert("No applicants are currently loaded.");
  //     return;
  //   }

  //   const confirmed = window.confirm(
  //     `This will migrate sponsor visibility for ${applicantEntries.length} loaded applicants by copying sponsorId into sponsorIds where needed.\n\nContinue?`,
  //   );
  //   if (!confirmed) {
  //     return;
  //   }

  //   setIsMigratingSponsorIds(true);

  //   let updated = 0;
  //   let skipped = 0;
  //   let missingSponsor = 0;
  //   let failed = 0;
  //   const updatedApplicants = { ...applicants };

  //   try {
  //     for (const [id, applicant] of applicantEntries) {
  //       if (!applicant.sponsorId) {
  //         missingSponsor += 1;
  //         continue;
  //       }

  //       const rawApplicant = { ...applicant };
  //       const existingSponsorIds =
  //         rawApplicant && Array.isArray(rawApplicant.sponsorIds)
  //           ? rawApplicant.sponsorIds.filter(Boolean)
  //           : [];
  //       const sponsorIds = Array.from(
  //         new Set([...existingSponsorIds, applicant.sponsorId].filter(Boolean)),
  //       );

  //       const isUnchanged =
  //         existingSponsorIds.length === sponsorIds.length &&
  //         sponsorIds.every((sponsorId) =>
  //           existingSponsorIds.includes(sponsorId),
  //         );

  //       if (isUnchanged) {
  //         skipped += 1;
  //         continue;
  //       }

  //       try {
  //         const applicantDoc: ApplicantDoc = {
  //           id,
  //           applicant: {
  //             ...applicant,
  //             sponsorIds,
  //           },
  //         };

  //         await storeApplicant(currentUser?.appUser, applicantDoc, false);
  //         updatedApplicants[id] = applicantDoc.applicant;
  //         updated += 1;
  //       } catch (error) {
  //         console.error(`Failed to migrate applicant ${id}`, error);
  //         failed += 1;
  //       }
  //     }

  //     setApplicants(updatedApplicants);
  //     window.alert(
  //       [
  //         "Sponsor visibility migration completed.",
  //         `Loaded applicants: ${applicantEntries.length}`,
  //         `Updated: ${updated}`,
  //         `Skipped: ${skipped}`,
  //         `Missing sponsorId: ${missingSponsor}`,
  //         `Failed: ${failed}`,
  //       ].join("\n"),
  //     );
  //   } finally {
  //     setIsMigratingSponsorIds(false);
  //   }
  // };

  return (
    <TitledPage title="Applicants">
      <Box>
        <Box display={"flex"} sx={{ gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleAddNewApplicant()}
          >
            Add New Applicant
          </Button>

          <AddApplicantDialog
            open={open}
            onClose={handleClose}
            onSave={handleSaveApplicant}
            applicant={
              editMode && selectedApplicantId
                ? {
                    id: selectedApplicantId,
                    applicant: applicants[selectedApplicantId],
                  }
                : null
            }
          />
          {hasRole(currentUser?.appUser, ["admin"]) && (
            <>
              <DocumentHistoryDialog
                open={openHistory}
                onClose={() => setOpenHistory(false)}
                documentId={historyDoc}
              />
              <Tooltip title="History">
                <span>
                  <IconButton
                    color="primary"
                    edge={"end"}
                    disabled={!selectedApplicantId}
                    onClick={() => handleShowHistory(selectedApplicantId)}
                  >
                    <InfoIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ marginTop: "8px", marginBottom: "4px" }}
          />
          {/* {hasRole(currentUser?.appUser, ["admin"]) && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => void handleCallFunction()}
              disabled={isMigratingSponsorIds}
            >
              {isMigratingSponsorIds
                ? "Migrating sponsorIds..."
                : "Migrate loaded sponsorIds"}
            </Button>
          )} */}
          <Button
            variant="outlined"
            color="primary"
            onClick={handleExport}
            disabled={isExporting}
            sx={{ display: "none" }}
          >
            Export new applicants
          </Button>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchValue}
            onChange={handleSearchValueChange}
            sx={{ minWidth: "300px" }}
          />
        </Box>

        <Dialog open={isExporting} onClose={() => {}} disableEscapeKeyDown>
          <DialogTitle>Exporting applicants</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                minWidth: "320px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography align="center">
                {exportProgress + " / " + exportTotal}
              </Typography>
              <LinearProgress
                variant={exportTotal ? "determinate" : "indeterminate"}
                value={
                  exportTotal ? (exportProgress / exportTotal) * 100 : undefined
                }
              />
            </Box>
          </DialogContent>
        </Dialog>

        {/* <Dialog
          open={emailCasingAlert.open}
          onClose={() =>
            setEmailCasingAlert((prev) => ({ ...prev, open: false }))
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Applicant email casing</DialogTitle>
          <DialogContent>
            <Alert severity={emailCasingAlert.severity} sx={{ mt: 0.5 }}>
              <Typography
                variant="body2"
                component="div"
                sx={{ whiteSpace: "pre-line" }}
              >
                {emailCasingAlert.message}
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setEmailCasingAlert((prev) => ({ ...prev, open: false }))
              }
            >
              OK
            </Button>
          </DialogActions>
        </Dialog> */}

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            marginTop: "1rem",
            minHeight: "400px",
            height: "calc(100vh - 210px)",
          }}
        >
          <Box sx={{ width: "70%", marginRight: "2%" }}>
            <TableVirtuoso
              ref={virtuosoRef}
              style={{ height: "100%" }}
              data={visibleRows}
              context={{
                selectedId: selectedApplicantId,
                handleSelect: handleSelectApplicant,
              }}
              components={VirtuosoTableComponents}
              fixedHeaderContent={() => (
                <EnhancedTableHeadContents
                  numSelected={selectedApplicantId ? 1 : 0}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={() => {}}
                  onRequestSort={handleRequestSort}
                  rowCount={visibleRows.length}
                  headCells={headCells}
                />
              )}
              itemContent={(index, [applicantId, applicant]) => (
                <>
                  <TableCell scope="row" size="small">
                    {applicant.name}
                  </TableCell>
                  <TableCell size="small">{applicant.surname}</TableCell>
                  <TableCell size="small">
                    {(applicant.nationality && applicant.nationality.label) ||
                      "n/a"}
                  </TableCell>
                  <TableCell align="right" style={{ width: 120 }} size="small">
                    <Tooltip title="Edit applicant">
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleEditApplicant({ id: applicantId, applicant });
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(applicantId || "");
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  <TableCell align="right" colSpan={headCells.length}>
                    {visibleRows.length + " applicants"}
                  </TableCell>
                </TableRow>
              )}
            />
          </Box>
          <Box sx={{ width: "48%" }}>
            {displayApplicantDetails(selectedApplicantId)}
          </Box>
        </Box>
      </Box>
    </TitledPage>
  );
};

export default ApplicantsPage;
